import { NavController } from '@ionic/angular';
import { UserDeviceService } from './../_core/user-device/user-device.service';
import { NavPage } from './../../models/_core/nav-page';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { User } from 'src/app/models/user';
import { environment } from 'src/environments/environment';
import { Platform } from '@ionic/angular';

/**
 * ID: bh-navigation
 * Name: BH Navigation Service
 * Description: Service used for managing navigation, main menus, and account options
 * Version: 1
 *
 * ==============================
 * Change Log
 * ==============================
 * 2022-15-25 - MW - v1: Initial development
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  env = environment;

  // Define your pages for this app here
  homeNavPage: NavPage = {
    name: 'Home',
    navPath: '/tabs/home',
    tabPath: 'home',
    tabId: 'home',
    ionIcon: 'home',
    roles: ['ADMIN', 'SYS_ADMIN', 'admin', 'sys_admin'],
    isAccessible: true,
    isActive: true
  };

  navPages: NavPage[] = [
    {
      name: 'Messages',
      navPath: '/tabs/messages',
      tabPath: 'messages',
      tabId: 'messages',
      ionIcon: 'mail',
      roles: ['ADMIN', 'SYS_ADMIN', 'admin', 'sys_admin'],
      isAccessible: true,
      isActive: true
    },
    {
      name: 'Health Record',
      navPath: '/tabs/health-record',
      tabPath: 'health-record',
      tabId: 'health-record',
      ionIcon: 'heart',
      roles: ['ADMIN', 'SYS_ADMIN', 'admin', 'sys_admin'],
      isAccessible: true,
      isActive: true
    },
    {
      name: 'Resources',
      navPath: '/tabs/resources',
      tabPath: 'resources',
      tabId: 'resources',
      ionIcon: 'library',
      roles: ['ADMIN', 'SYS_ADMIN', 'admin', 'sys_admin'],
      isAccessible: true,
      isActive: true
    },
    {
      name: 'Account',
      navPath: '/tabs/account',
      tabPath: 'account',
      tabId: 'account',
      ionIcon: 'person-circle',
      roles: ['ADMIN', 'SYS_ADMIN', 'admin', 'sys_admin'],
      isAccessible: true,
      isActive: true
    },
  ];

  navPagesSubject: BehaviorSubject<NavPage[]> = new BehaviorSubject([]);
  showNotifications = false;
  subscriptions: Subscription[] = [];
  accessiblePages = 0;
  navigationLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private navCtrl: NavController,
    private deviceService: UserDeviceService,
    private platform: Platform,
  ) {
    if (this.homeNavPage.isActive) {
      this.navPages.unshift(this.homeNavPage);
    }
    this.navPagesSubject.next(this.navPages);
   }

  checkPrivileges(authUser: User) {
    this.navPages.forEach(page => {
      if (page.roles && page.roles.length > 0) {
        if (authUser) {
          // User logged in, check roles
          const matches = page.roles.filter(r =>
            (r && authUser && authUser.role && r.toLowerCase() === authUser.role.toLowerCase())
          );
          page.isAccessible = (matches.length > 0);
        } else {
          // User not logged in
          page.isAccessible = false;
        }
      } else {
        // Page unprotected
        page.isAccessible = true;
      }
    });

    this.accessiblePages = this.navPages.filter(p => p.isAccessible).length;
    this.navPagesSubject.next(this.navPages);
  }

  navigateHome(): Promise<boolean> {
    const homePage = this.navPages.length > 0 ? this.navPages[0].navPath : '/';
    return this.navigateBack(homePage);
  }

  navigateBasedOnRole(authUser: User): Promise<boolean> {
    // Check if device is mobile/tablet first
    const userDevice = this.deviceService.getUserDevice();
    const isMobileOrTablet = this.isMobileOrTabletDevice(userDevice);

    if (isMobileOrTablet) {
      // Always route mobile/tablet users to survey-selection regardless of role
      return this.navigateRoot('/tabs/survey-selection');
    }

    // Desktop routing based on role
    if (authUser && authUser.role) {
      const role = authUser.role.toLowerCase();
      if (role === 'user') {
        return this.navigateRoot('/tabs/survey-selection');
      }
      else if (role === 'admin' || role === 'sys_admin') {
        return this.navigateRoot('/tabs/surveys');
      }
    }
    // Default to home page for admin/sys_admin roles or if no role is specified
    return this.navigateHome();
  }

  navigateForward(pathUrl, navOptions = undefined): Promise<boolean> {
    const userDevice = this.deviceService.getUserDevice();
    return this.navCtrl.navigateForward(pathUrl, navOptions);
  }

  navigateBack(pathUrl, navOptions = undefined): Promise<boolean> {
    const userDevice = this.deviceService.getUserDevice();
    return this.navCtrl.navigateBack(pathUrl, navOptions);
  }

  navigateRoot(pathUrl, navOptions = undefined): Promise<boolean> {
    return this.navCtrl.navigateRoot(pathUrl, navOptions);
  }

  /**
   * Checks if the current device is an iOS device (iPad or iPhone)
   * @param deviceInfo Optional device information to check
   * @returns boolean
   */
  private isIOSDevice(deviceInfo?: any): boolean {
    // First check if we have device info passed as parameter
    if (deviceInfo && deviceInfo.os) {
      const os = deviceInfo.os.toLowerCase();
      return os === 'ios';
    }

    // Fallback: check device service
    const userDevice = this.deviceService.getUserDevice();
    if (userDevice && userDevice.os) {
      const os = userDevice.os.toLowerCase();
      return os === 'ios';
    }

    // Final fallback: check platform
    return this.platform.is('ios');
  }

  /**
   * Checks if the current device is a mobile or tablet device
   * @param deviceInfo Optional device information to check
   * @returns boolean
   */
  private isMobileOrTabletDevice(deviceInfo?: any): boolean {
    // Use provided device info or get from service
    const device = deviceInfo || this.deviceService.getUserDevice();

    if (!device) {
      return false;
    }

    // Check if it's a mobile device (native app)
    if (device.isMobileDevice) {
      return true;
    }

    // Check if it's a narrow viewport (mobile/tablet width)
    if (device.isNarrowViewport) {
      return true;
    }

    // Check if it's iOS (iPad/iPhone)
    if (device.os && device.os.toLowerCase() === 'ios') {
      return true;
    }

    // Check if it's Android
    if (device.os && device.os.toLowerCase() === 'android') {
      return true;
    }

    return false;
  }

  /**
   * Checks if a navigation path is allowed on the current device
   * @param pathUrl string
   * @param deviceInfo Optional device information to check
   * @returns boolean
   */
  isNavigationAllowed(pathUrl: string, deviceInfo?: any): boolean {
    // If not an iOS device, allow all navigation
    if (!this.isIOSDevice(deviceInfo)) {
      return true;
    }

    // Allowed paths for iOS devices
    const allowedPaths = [
      '/login',
      '/survey-selection',
      '/tabs/survey-selection',
      '/survey-langauge-pick',
      '/tabs/survey-language-pick',
      '/survey-questionnaire',
      '/tabs/surveys/questionnaire',
      '/redirect'
    ];

    // Check exact matches
    if (allowedPaths.includes(pathUrl)) {
      return true;
    }

    // Check for parameterized routes (e.g., /survey-langauge-pick/123)
    for (const allowedPath of allowedPaths) {
      if (this.matchesParameterizedRoute(pathUrl, allowedPath)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Checks if device information is available
   * @param deviceInfo Optional device information to check
   * @returns boolean
   */
  isDeviceInfoAvailable(deviceInfo?: any): boolean {
    // First check if we have device info passed as parameter
    if (deviceInfo && deviceInfo.os) {
      return true;
    }

    // Fallback: check device service
    const userDevice = this.deviceService.getUserDevice();
    return !!(userDevice && userDevice.os);
  }

  /**
   * Checks if a path matches a parameterized route pattern
   * @param path string
   * @param pattern string
   * @returns boolean
   */
  private matchesParameterizedRoute(path: string, pattern: string): boolean {
    // Convert pattern to regex
    const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(path);
  }

  /**
   * Navigates to a path with device-specific restrictions
   * @param pathUrl string
   * @param navOptions any
   * @returns Promise<boolean>
   */
  navigateWithDeviceRestrictions(pathUrl: string, navOptions = undefined): Promise<boolean> {
    if (this.isNavigationAllowed(pathUrl)) {
      return this.navigateForward(pathUrl, navOptions);
    } else {
      console.log(`Navigation blocked: ${pathUrl} is not allowed on iOS device`);
      // Redirect to survey-selection for iOS devices
      return this.navigateRoot('/tabs/survey-selection');
    }
  }

}
